import { useCallback, useEffect, useState } from 'react'
import { type Step, isStep, DEFAULT_STEP } from './types.js'

const STEP_PARAM = 'step'
const STEP_CHANGED_EVENT = 'veritact:step-changed'

const readFromUrl = (): Step => {
  const v = new URLSearchParams(window.location.search).get(STEP_PARAM)
  return isStep(v) ? v : DEFAULT_STEP
}

export interface UseStepResult {
  step: Step
  goto: (s: Step) => void
}

export const useStep = (): UseStepResult => {
  const [step, setStep] = useState<Step>(readFromUrl)

  useEffect(() => {
    // Sync from URL on mount in case a child component's effect already changed
    // the URL before our listener was attached (child effects run before parent's).
    setStep(readFromUrl())
    const onChange = (): void => setStep(readFromUrl())
    window.addEventListener('popstate', onChange)
    window.addEventListener(STEP_CHANGED_EVENT, onChange)
    return () => {
      window.removeEventListener('popstate', onChange)
      window.removeEventListener(STEP_CHANGED_EVENT, onChange)
    }
  }, [])

  const goto = useCallback((next: Step) => {
    const params = new URLSearchParams(window.location.search)
    params.set(STEP_PARAM, next)
    const url = `${window.location.pathname}?${params.toString()}`
    window.history.pushState({}, '', url)
    setStep(next)
    window.dispatchEvent(new Event(STEP_CHANGED_EVENT))
  }, [])

  return { step, goto }
}
